import { endOfDay, startOfDay } from 'date-fns';

import { type SearchFilterAssetCustomFields, type SearchFilterDate } from '@air/api/types';

import { FilterParamNames } from '~/constants/search';
import {
  type CustomFieldSelectedFilter,
  type DateCustomFieldFilter,
  type DateCustomFieldOperator,
} from '~/store/filters/types';

export const DATE_CUSTOM_FIELD_OPERATORS: readonly DateCustomFieldOperator[] = ['range', 'notSet'];

const dateCustomFieldOperators = new Set<string>(DATE_CUSTOM_FIELD_OPERATORS);

export const DATE_CUSTOM_FIELD_OPERATOR_LABELS: Record<DateCustomFieldOperator, string> = {
  range: 'Is between',
  notSet: 'Is empty',
};

const DATE_CUSTOM_FIELD_URL_PREFIXES = [FilterParamNames.cfDateStart, FilterParamNames.cfDateEnd];

export const isDateCustomFieldOperator = (value: string): value is DateCustomFieldOperator =>
  dateCustomFieldOperators.has(value);

export const isDateCustomFieldUrlParam = (paramName: string) =>
  DATE_CUSTOM_FIELD_URL_PREFIXES.some((prefix) => paramName.startsWith(prefix));

export const isCustomFieldDateInstant = (value: string) => value.includes('T') && !Number.isNaN(Date.parse(value));

export const customFieldDateInstantFromCalendarDay = ({ day, edge }: { day: Date; edge: 'start' | 'end' }) =>
  (edge === 'start' ? startOfDay(day) : endOfDay(day)).toISOString();

export const calendarDateFromCustomFieldInstant = (instant: string | undefined) => {
  if (!instant || !isCustomFieldDateInstant(instant)) {
    return undefined;
  }

  const date = new Date(instant);

  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return date;
};

export const dateRangeFromBounds = ({ start, end }: SearchFilterDate): SearchFilterDate | null => {
  const nextStart = start && isCustomFieldDateInstant(start) ? start : undefined;
  const nextEnd = end && isCustomFieldDateInstant(end) ? end : undefined;
  const hasInvalidBound = Boolean((start && !nextStart) || (end && !nextEnd));

  if (hasInvalidBound || (!nextStart && !nextEnd)) {
    return null;
  }

  if (nextStart && nextEnd && Date.parse(nextStart) > Date.parse(nextEnd)) {
    return null;
  }

  return {
    ...(nextStart ? { start: nextStart } : {}),
    ...(nextEnd ? { end: nextEnd } : {}),
  };
};

export const dateStateFromDateFilter = (date: SearchFilterDate): DateCustomFieldFilter | null => {
  const range = dateRangeFromBounds(date);

  if (!range) {
    return null;
  }

  return {
    operator: 'range',
    ...range,
  };
};

export const toAssetCustomFieldDateFilter = (
  date: DateCustomFieldFilter,
): SearchFilterAssetCustomFields['filter'] | null => {
  switch (date.operator) {
    case 'notSet':
      return { notSet: true };
    case 'range': {
      const range = dateRangeFromBounds(date);

      if (!range) {
        return null;
      }

      return { date: range };
    }
    default: {
      const exhaustiveOperator: never = date;

      return exhaustiveOperator;
    }
  }
};

export const dateCustomFieldAppliedCount = (date: SearchFilterDate) => (dateRangeFromBounds(date) ? 1 : 0);

export const dateCustomFieldQueryEntries = (customField: CustomFieldSelectedFilter): [string, string[]][] => {
  if (!customField.date) {
    return [];
  }

  const filter = toAssetCustomFieldDateFilter(customField.date);

  if (!filter) {
    return [];
  }

  if ('notSet' in filter && filter.notSet) {
    return [[`${FilterParamNames.cfNotSet}${customField.id}`, ['true']]];
  }

  if (!('date' in filter)) {
    return [];
  }

  const entries: [string, string[]][] = [];

  if (filter.date.start) {
    entries.push([`${FilterParamNames.cfDateStart}${customField.id}`, [filter.date.start]]);
  }

  if (filter.date.end) {
    entries.push([`${FilterParamNames.cfDateEnd}${customField.id}`, [filter.date.end]]);
  }

  return entries;
};

export const mergeAssetCustomFieldFilters = (
  filters: SearchFilterAssetCustomFields[],
): SearchFilterAssetCustomFields[] => {
  const notSetIds = new Set(
    filters.flatMap((field) => ('notSet' in field.filter && field.filter.notSet ? [field.id] : [])),
  );
  const dateBounds = new Map<string, SearchFilterDate>();
  const rest = filters.filter((field) => !('date' in field.filter));

  filters.forEach((field) => {
    if (!('date' in field.filter) || notSetIds.has(field.id)) {
      return;
    }

    const current = dateBounds.get(field.id) ?? {};

    if (field.filter.date.start) {
      current.start = field.filter.date.start;
    }

    if (field.filter.date.end) {
      current.end = field.filter.date.end;
    }

    dateBounds.set(field.id, current);
  });

  const mergedDates = [...dateBounds.entries()].flatMap(([id, bounds]) => {
    const range = dateRangeFromBounds(bounds);

    if (!range) {
      return [];
    }

    return [{ id, filter: { date: range } }];
  });

  return [...rest, ...mergedDates];
};
